/**
 * Compara 2–3 opções no mercado (tamanho, marca, varejo/atacado).
 * Preço por medida = preço / quantidade.
 * Opcional: dias para acabar o maior pacote + validade → alerta de desperdício.
 */
export function simularCompararPrecosMercado({
    opcoes,
    unidadeCurta = 'un.',
    diasParaAcabarMaior = null,
    validadeDias = null,
}) {
    if (!Array.isArray(opcoes) || opcoes.length < 2) return null;

    const validas = opcoes
        .map((o, index) => {
            const preco = Number(o.preco);
            const medida = Number(o.medida);
            const label = o.label || `Opção ${index + 1}`;
            if (!Number.isFinite(preco) || preco <= 0 || !Number.isFinite(medida) || medida <= 0) {
                return null;
            }
            return {
                label,
                preco,
                medida,
                precoPorUnidade: preco / medida,
            };
        })
        .filter(Boolean);

    if (validas.length < 2) return null;

    const ranking = [...validas].sort((a, b) => a.precoPorUnidade - b.precoPorUnidade);
    const melhor = ranking[0];
    const pior = ranking[ranking.length - 1];
    const economiaVsPior = pior.precoPorUnidade - melhor.precoPorUnidade;
    const empate = ranking.every(
        (o) => Math.abs(o.precoPorUnidade - melhor.precoPorUnidade) < 1e-9
    );

    const maiorPacote = validas.reduce((acc, o) => (o.medida > acc.medida ? o : acc), validas[0]);
    const economiaNoMaior = (pior.precoPorUnidade - melhor.precoPorUnidade) * maiorPacote.medida;

    let alertaDesperdicio = null;
    const dias = diasParaAcabarMaior != null ? Number(diasParaAcabarMaior) : null;
    const validade = validadeDias != null ? Number(validadeDias) : null;

    if (Number.isFinite(dias) && dias > 0 && Number.isFinite(validade) && validade > 0 && dias > validade) {
        alertaDesperdicio =
            `Você levaria cerca de ${Math.ceil(dias)} dias para terminar o pacote maior (${maiorPacote.label}), ` +
            `mas a validade é de ${validade} dias. Parte pode estragar — a “economia” pode virar desperdício.`;
    }

    let veredicto;
    if (empate) {
        veredicto = {
            tipo: 'empate',
            titulo: 'Empate no preço',
            detalhe: `As opções custam o mesmo por ${unidadeCurta}.`,
        };
    } else if (alertaDesperdicio && melhor.label === maiorPacote.label) {
        veredicto = {
            tipo: 'atencao',
            titulo: `${melhor.label} é mais barata no papel`,
            detalhe:
                `${alertaDesperdicio} Sem estragar nada, a diferença seria cerca de ` +
                `${economiaNoMaior.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.`,
        };
    } else if (alertaDesperdicio) {
        veredicto = {
            tipo: 'atencao',
            titulo: `Mais barata: ${melhor.label}`,
            detalhe:
                `${melhor.precoPorUnidade.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} por ${unidadeCurta}. ` +
                `${alertaDesperdicio}`,
        };
    } else {
        veredicto = {
            tipo: 'positivo',
            titulo: `Mais barata: ${melhor.label}`,
            detalhe:
                `${melhor.precoPorUnidade.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} por ${unidadeCurta} — ` +
                `${economiaVsPior.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} a menos que a mais cara.`,
        };
    }

    return {
        opcoes: validas,
        ranking,
        melhor,
        pior,
        empate,
        economiaVsPior,
        maiorPacote,
        alertaDesperdicio,
        unidadeCurta,
        veredicto,
    };
}
